import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import Alart from "../../../components/Alart";
import TableItem from "../../../components/TableItem";
import { getSalesPersonByPaging } from "../../../services/SalesPerson";

// Import icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "ACTIVE", label: "Active" },
  { value: "DIACTIVE", label: "Inactive" },
];

let _salesmanListState = null;

function Salesman() {
  const history = useHistory();

  // State management
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openForm, setOpenForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Alert state for disable warning
  const [openDisableWarning, setOpenDisableWarning] = useState(false);
  const [msgDisableWarning, setMsgDisableWarning] = useState({
    title: "Warning",
    content: "Confirm You Need To Disable This Sales Person",
  });
  const [disableItemId, setDisableItemId] = useState(null);

  // Filter state
  const [filter, setFilter] = useState({
    _name: "",
    _email: "",
    _status: "",
    _address: "",
  });

  // Table columns configuration
  const columns = [
    {
      id: "name",
      label: "Name",
      minWidth: 120,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PersonIcon sx={{ fontSize: 16, mr: 1, color: "primary.main" }} />
          <Typography fontWeight="medium">{value}</Typography>
        </Box>
      ),
    },
    {
      id: "phone",
      label: "Phone",
      minWidth: 120,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PhoneIcon sx={{ fontSize: 16, mr: 1, color: "info.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "email",
      label: "Email",
      minWidth: 180,
      format: (value) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <EmailIcon sx={{ fontSize: 16, mr: 1, color: "warning.main" }} />
          {value}
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value === "ACTIVE" ? "success" : "error"}
          size="small"
          variant={value === "DIACTIVE" ? "outlined" : "filled"}
        />
      ),
    },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  // Create data for table
  function createData(name, phone, email, status, action, customer_id) {
    return { name, phone, email, status, action, customer_id };
  }

  // Initial data load
  useEffect(() => {
    if (_salesmanListState) {
      const { page: savedPage, filter: savedFilter } = _salesmanListState;
      _salesmanListState = null;
      setPage(savedPage);
      setFilter(savedFilter);
      fetchData(savedFilter, savedPage);
    } else {
      fetchData(filter, page);
    }
  }, []);

  // Fetch data from API
  const fetchData = async (salesmanFilter, pageNo) => {
    setLoading(true);
    try {
      let result = await getSalesPersonByPaging(
        pageNo,
        rowsPerPage,
        salesmanFilter
      );
      let salesmanSet = result.content;

      // Set total rows and pages for pagination
      setTotalPages(result.totalPages);
      setTotalRows(result.totalElements || 0);

      const newSet = [];
      if (salesmanSet) {
        for (let x = 0; x < salesmanSet.length; x++) {
          // Set data in new set list to display in the table
          newSet.push(
            createData(
              salesmanSet[x].fname + " " + salesmanSet[x].lname,
              salesmanSet[x].phone,
              salesmanSet[x].email,
              salesmanSet[x].locked ? "DIACTIVE" : "ACTIVE",
              salesmanSet[x].userId
            )
          );
        }
      }

      // Set rows to table
      setRows(newSet);
    } catch (error) {
      console.error("Error fetching salesperson data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination handler
  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Handle action for table actions
  const handleAction = async (event, id) => {
    console.log("Salemsan Id", id);
    switch (event) {
      case "view":
        _salesmanListState = { page, filter };
        history.push("/template/salesman_update/" + id);
        break;
      default:
        return;
    }
  };

  // Disable salesperson function
  const disableProduct = async (id) => {
    setDisableItemId(id);
    setOpenDisableWarning(true);
  };

  // Disable warning action buttons
  function DisableWarningActionButtons() {
    return (
      <>
        <ActionButton
          color="error"
          variant="contained"
          onClick={async () => {
            // await DiactivateItemById(disableItemId);
            setOpenDisableWarning(false);
            await fetchData(filter, page);
          }}
        >
          Disable
        </ActionButton>
        <ActionButton
          color="primary"
          variant="outlined"
          onClick={() => {
            setOpenDisableWarning(false);
          }}
        >
          Cancel
        </ActionButton>
      </>
    );
  }

  // Reset filters
  const resetFilters = () => {
    setFilter({
      _name: "",
      _email: "",
      _status: "",
      _address: "",
    });
  };

  // Handle filter change
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <>
      <Alart
        open={openDisableWarning}
        message={msgDisableWarning}
        actions={<DisableWarningActionButtons />}
      />

      <StyledPaper elevation={0}>
        {openForm ? null : ( // Add Salesperson Form would go here
          <>
            <HeaderCard>
              <Grid container alignItems="center" spacing={2}>
                <Grid item>
                  <PersonIcon fontSize="large" />
                </Grid>
                <Grid item xs>
                  <Typography variant="h5" fontWeight="bold">
                    Sales Persons
                  </Typography>
                  <Typography variant="body2">
                    View and manage your sales team information
                  </Typography>
                </Grid>
              </Grid>
            </HeaderCard>

            <Box
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <TextField
                placeholder="Search by name..."
                variant="outlined"
                size="small"
                sx={{ width: "40%" }}
                value={filter._name}
                onChange={(e) => {
                  handleFilterChange("_name", e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title="Toggle Filters">
                  <ActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={toggleFilters}
                  >
                    Filters
                  </ActionButton>
                </Tooltip>
                <Chip
                  label={`${totalRows} Sales Persons`}
                  variant="outlined"
                  color="primary"
                />
              </Stack>
            </Box>

            {showFilters && (
              <FilterContainer>
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Advanced Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_email"
                      value={filter._email}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Email"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Status"
                      name="_status"
                      value={filter._status}
                      onChange={(event) =>
                        handleFilterChange(
                          event.target.name,
                          event.target.value
                        )
                      }
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <InputField
                      name="_address"
                      value={filter._address}
                      onChange={(event, newInputValue) =>
                        handleFilterChange(event, newInputValue)
                      }
                      type="text"
                      label="Address"
                      fullWidth
                    />
                  </Grid>
                </Grid>
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Tooltip title="Reset Filters">
                      <ActionButton
                        variant="outlined"
                        color="secondary"
                        startIcon={<RestartAltIcon />}
                        onClick={resetFilters}
                      >
                        Reset
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="Apply Filters">
                      <ActionButton
                        variant="contained"
                        color="primary"
                        onClick={() => fetchData(filter, page)}
                      >
                        Apply Filters
                      </ActionButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </FilterContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ textAlign: "center", p: 5 }}>
                <Typography variant="body1" color="textSecondary">
                  No sales persons found. Try adjusting your filters or add a
                  new sales person.
                </Typography>
              </Box>
            ) : (
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={false}
                totalPages={totalPages}
                handleAction={handleAction}
                showActions={{
                  view: "Salesman view",
                }}
                actionRoles={{
                  view: ["SALESMAN_VIEW", "ROLE_ADMIN"],
                }}
                tableProps={{
                  sx: {
                    "& .MuiTableRow-root:hover": {
                      backgroundColor: alpha("#000", 0.04),
                    },
                  },
                }}
              />
            )}

            {totalPages > 1 && (
              <PaginationContainer>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={handleChange}
                  size="medium"
                />
              </PaginationContainer>
            )}
          </>
        )}
      </StyledPaper>
    </>
  );
}

export default Salesman;
